/**
 * Client for the configuration export, preview and apply (`/api/admin/configuration`). Export answers with a file to save; preview
 * and apply take the file and its passphrase as multipart form fields, never in the query string. Apply also takes the fingerprint
 * of the preview it is applying, and the server refuses it when the plan has changed since.
 *
 * Refusals keep the server's message, because an administrator needs to know why a file was refused. The passphrase is only ever
 * placed in the request body; this module never stores it.
 */
import { apiFetch } from "./apiFetch";

/** The largest configuration file the server accepts, in bytes. Checked on the client too, so the message comes before the upload. */
export const maxConfigurationFileBytes = 25 * 1024 * 1024;

/** The shortest passphrase an export or an import accepts. */
export const minimumPassphraseLength = 12;

/** One row of a preview, with what would happen to it. */
export interface ConfigurationPreviewRow {
  key: string;
  status: "Created" | "Updated" | "Unchanged" | "Refused" | "NotInSource";
  detail: string | null;
  changes: string[];
}

/** One kind's counts and rows in a preview. */
export interface ConfigurationPreviewKind {
  kind: string;
  created: number;
  updated: number;
  unchanged: number;
  refused: number;
  notInSource: number;
  rows: ConfigurationPreviewRow[];
}

/** An allocation that would sync to a vendor once the kill switch is turned off. */
export interface ConfigurationVendorEffect {
  email: string;
  provider: string;
  change: string;
}

/** What applying a file would do. Nothing has been written. */
export interface ConfigurationPreview {
  instanceName: string;
  exportedAtUtc: string;
  expiresAtUtc: string;
  killSwitchOn: boolean;
  canApply: boolean;
  blockingReasons: string[];
  fingerprint: string;
  kinds: ConfigurationPreviewKind[];
  newPeopleGettingAccess: string[];
  vendorEffect: ConfigurationVendorEffect[];
}

/** One kind's counts after an apply. */
export interface ConfigurationAppliedKind {
  kind: string;
  created: number;
  updated: number;
  unchanged: number;
  notInSource: number;
}

/** The outcome of an apply. */
export interface ConfigurationApplied {
  instanceName: string;
  fingerprint: string;
  created: number;
  updated: number;
  auditEntries: number;
  kinds: ConfigurationAppliedKind[];
}

/** A saved export: the file's bytes and the name the server gave it. */
export interface ConfigurationExported {
  blob: Blob;
  fileName: string;
}

/** The outcome of one configuration call. */
export type ConfigurationResult<TResponse> =
  | { kind: "ok"; response: TResponse }
  | { kind: "denied" }
  | { kind: "refused"; message: string }
  | { kind: "conflict"; message: string }
  | { kind: "error"; message: string };

const exportPath = "/api/admin/configuration/export";
const previewPath = "/api/admin/configuration/preview";
const applyPath = "/api/admin/configuration/apply";

interface ProblemBody {
  detail?: string;
  title?: string;
}

/**
 * Builds the multipart body the preview and apply endpoints read. It is the only place a file and passphrase become a request.
 *
 * @param file - The configuration file.
 * @param passphrase - The passphrase the file was sealed under.
 * @param expectedFingerprint - The fingerprint of the preview being applied, when there is one.
 * @returns The form data.
 */
function configurationForm(file: File, passphrase: string, expectedFingerprint?: string): FormData {
  const form = new FormData();
  form.append("file", file, file.name);
  form.append("passphrase", passphrase);
  if (expectedFingerprint !== undefined) {
    form.append("expectedFingerprint", expectedFingerprint);
  }

  return form;
}

async function readProblem(response: Response): Promise<ProblemBody> {
  try {
    return (await response.json()) as ProblemBody;
  } catch {
    return {};
  }
}

/**
 * Reads the file name from a `Content-Disposition` header, accepting the quoted and unquoted forms.
 *
 * @param header - The header value, or `null` when the response has none.
 * @returns The file name, or a fixed fallback when the header does not name one.
 */
export function fileNameFrom(header: string | null): string {
  const match = header?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match?.[1] ?? "aia-config.aiic";
}

/**
 * Exports the configuration as a passphrase-encrypted file.
 *
 * @param passphrase - The passphrase to seal the file under.
 * @returns The file, to be saved by the caller, or why it was not produced.
 */
export async function exportConfiguration(passphrase: string): Promise<ConfigurationResult<ConfigurationExported>> {
  try {
    const response = await apiFetch(exportPath, {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: { "Content-Type": "application/json", Accept: "application/octet-stream" },
      body: JSON.stringify({ passphrase }),
    });

    if (response.status === 403) {
      return { kind: "denied" };
    }

    if (response.status === 200) {
      const blob = await response.blob();
      return { kind: "ok", response: { blob, fileName: fileNameFrom(response.headers.get("Content-Disposition")) } };
    }

    const problem = await readProblem(response);
    if (response.status === 400) {
      return { kind: "refused", message: problem.detail ?? "The export request was not valid." };
    }

    return { kind: "error", message: problem.detail ?? `The export endpoint returned ${String(response.status)}.` };
  } catch {
    return { kind: "error", message: "The export endpoint could not be reached." };
  }
}

async function multipartRequest<TResponse>(path: string, form: FormData, label: string): Promise<ConfigurationResult<TResponse>> {
  try {
    const response = await apiFetch(path, {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json" },
      body: form,
    });

    if (response.status === 403) {
      return { kind: "denied" };
    }

    if (response.status === 200) {
      return { kind: "ok", response: (await response.json()) as TResponse };
    }

    const problem = await readProblem(response);
    if (response.status === 400) {
      return { kind: "refused", message: problem.detail ?? `The ${label} was refused.` };
    }

    if (response.status === 409) {
      return { kind: "conflict", message: problem.detail ?? `The ${label} no longer matches the preview.` };
    }

    return { kind: "error", message: problem.detail ?? `The ${label} endpoint returned ${String(response.status)}.` };
  } catch {
    return { kind: "error", message: `The ${label} endpoint could not be reached.` };
  }
}

/**
 * Opens and plans an uploaded configuration file. Changes nothing.
 *
 * @param file - The configuration file.
 * @param passphrase - The passphrase the file was sealed under.
 * @returns The preview, or why the file was refused.
 */
export function previewConfiguration(file: File, passphrase: string): Promise<ConfigurationResult<ConfigurationPreview>> {
  return multipartRequest<ConfigurationPreview>(previewPath, configurationForm(file, passphrase), "configuration preview");
}

/**
 * Applies a previewed file. The server refuses unless the plan still has the fingerprint the caller presents.
 *
 * @param file - The configuration file that was previewed.
 * @param passphrase - The passphrase the file was sealed under.
 * @param expectedFingerprint - The fingerprint of the latest preview.
 * @returns The counts written, or why the apply did not happen.
 */
export function applyConfiguration(file: File, passphrase: string, expectedFingerprint: string): Promise<ConfigurationResult<ConfigurationApplied>> {
  return multipartRequest<ConfigurationApplied>(applyPath, configurationForm(file, passphrase, expectedFingerprint), "configuration apply");
}
